import { useState, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveImage } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function BeforeAfterSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const variant = config.variant || 'default';
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);

    const [position, setPosition] = useState(50);
    const containerRef = useRef(null);
    const isDragging = useRef(false);
    const [showAfter, setShowAfter] = useState(false);

    const updatePosition = useCallback((clientX) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        setPosition((x / rect.width) * 100);
    }, []);

    const updatePositionY = useCallback((clientY) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const y = Math.max(0, Math.min(clientY - rect.top, rect.height));
        setPosition((y / rect.height) * 100);
    }, []);

    const handleMouseDown = (e) => { isDragging.current = true; updatePosition(e.clientX); };
    const handleMouseMove = (e) => { if (isDragging.current) updatePosition(e.clientX); };
    const handleMouseUp = () => { isDragging.current = false; };
    const handleTouchStart = (e) => { isDragging.current = true; updatePosition(e.touches[0].clientX); };
    const handleTouchMove = (e) => { if (isDragging.current) { e.preventDefault(); updatePosition(e.touches[0].clientX); } };

    const handleMouseDownY = (e) => { isDragging.current = true; updatePositionY(e.clientY); };
    const handleMouseMoveY = (e) => { if (isDragging.current) updatePositionY(e.clientY); };
    const handleTouchStartY = (e) => { isDragging.current = true; updatePositionY(e.touches[0].clientY); };
    const handleTouchMoveY = (e) => { if (isDragging.current) { e.preventDefault(); updatePositionY(e.touches[0].clientY); } };

    const beforeUrl = resolveImage(content, 'before', mediaFolders) || content.before_url || '';
    const afterUrl = resolveImage(content, 'after', mediaFolders) || content.after_url || '';
    const beforeLabel = content.before_label || 'Antes';
    const afterLabel = content.after_label || 'Después';

    const BeforeImg = ({ className = "absolute inset-0 w-full h-full object-cover" }) => beforeUrl
        ? <img src={beforeUrl} alt={beforeLabel} className={className} loading="lazy" />
        : <div className={`${className} bg-gray-300 flex items-center justify-center`}><span className="text-gray-500 text-sm">{beforeLabel}</span></div>;

    const AfterImg = ({ className = "absolute inset-0 w-full h-full object-cover" }) => afterUrl
        ? <img src={afterUrl} alt={afterLabel} className={className} loading="lazy" />
        : <div className={`${className} bg-gray-200 flex items-center justify-center`}><span className="text-gray-400 text-sm">{afterLabel}</span></div>;

    /* ── variant: vertical ── */
    if (variant === 'vertical') {
        return (
            <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div
                        ref={containerRef}
                        className="relative w-full max-w-4xl mx-auto aspect-[16/10] rounded-2xl overflow-hidden cursor-row-resize select-none shadow-2xl"
                        onMouseDown={handleMouseDownY}
                        onMouseMove={handleMouseMoveY}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStartY}
                        onTouchMove={handleTouchMoveY}
                        onTouchEnd={handleMouseUp}
                    >
                        {/* After image (full) */}
                        <AfterImg />

                        {/* Before image (clipped from top) */}
                        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 ${100 - position}% 0)` }}>
                            <BeforeImg />
                        </div>

                        {/* Horizontal divider line */}
                        <div className="absolute left-0 right-0 h-1 -translate-y-1/2 z-10" style={{ top: `${position}%`, backgroundColor: s.accent }}>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                                style={{ backgroundColor: s.accent }}>
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 8l-4-4-4 4M9 16l4 4 4-4" />
                                </svg>
                            </div>
                        </div>

                        {/* Labels */}
                        <div className="absolute top-4 left-4 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
                            {beforeLabel}
                        </div>
                        <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
                            {afterLabel}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom" config={config}/>
            </section>
        );
    }

    /* ── variant: side-by-side ── */
    if (variant === 'side-by-side') {
        return (
            <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-w-5xl mx-auto">
                        {/* Before */}
                        <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                            {beforeUrl ? (
                                <img src={beforeUrl} alt={beforeLabel} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                                    <span className="text-gray-500 text-sm">{beforeLabel}</span>
                                </div>
                            )}
                            <div className="absolute top-3 left-3 text-white text-xs font-semibold px-2.5 py-1 rounded-md backdrop-blur-sm z-10"
                                style={{ backgroundColor: s.accent + 'cc' }}>
                                {beforeLabel}
                            </div>
                        </div>
                        {/* After */}
                        <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                            {afterUrl ? (
                                <img src={afterUrl} alt={afterLabel} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-gray-400 text-sm">{afterLabel}</span>
                                </div>
                            )}
                            <div className="absolute top-3 left-3 text-white text-xs font-semibold px-2.5 py-1 rounded-md backdrop-blur-sm z-10"
                                style={{ backgroundColor: s.accent + 'cc' }}>
                                {afterLabel}
                            </div>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom" config={config}/>
            </section>
        );
    }

    /* ── variant: fade-toggle ── */
    if (variant === 'fade-toggle') {
        return (
            <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="max-w-4xl mx-auto">
                        <div
                            className="relative aspect-[16/10] rounded-2xl overflow-hidden shadow-2xl"
                            onMouseEnter={() => setShowAfter(true)}
                            onMouseLeave={() => setShowAfter(false)}
                        >
                            {/* Before image (base) */}
                            <BeforeImg />

                            {/* After image (fades in) */}
                            <div className="absolute inset-0 transition-opacity duration-500" style={{ opacity: showAfter ? 1 : 0 }}>
                                <AfterImg />
                            </div>

                            {/* Current state label */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
                                {showAfter ? afterLabel : beforeLabel}
                            </div>
                        </div>

                        {/* Toggle button (mobile-friendly) */}
                        <div className="flex justify-center mt-4">
                            <button
                                type="button"
                                onClick={() => setShowAfter(v => !v)}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-colors shadow-md"
                                style={{ backgroundColor: s.accent }}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                                </svg>
                                {showAfter ? `Ver ${beforeLabel.toLowerCase()}` : `Ver ${afterLabel.toLowerCase()}`}
                            </button>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom" config={config}/>
            </section>
        );
    }

    /* ── variant: default (horizontal slider) ── */
    return (
        <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}

                <div
                    ref={containerRef}
                    className="relative w-full max-w-4xl mx-auto aspect-[16/10] rounded-2xl overflow-hidden cursor-col-resize select-none shadow-2xl"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleMouseUp}
                >
                    {/* After image (full) */}
                    <AfterImg />

                    {/* Before image (clipped) */}
                    <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}>
                        <BeforeImg />
                    </div>

                    {/* Divider line */}
                    <div className="absolute top-0 bottom-0 w-1 -translate-x-1/2 z-10" style={{ left: `${position}%`, backgroundColor: s.accent }}>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                            style={{ backgroundColor: s.accent }}>
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4M16 15l-4 4-4-4" />
                            </svg>
                        </div>
                    </div>

                    {/* Labels */}
                    <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
                        {beforeLabel}
                    </div>
                    <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
                        {afterLabel}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
